import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useSocialSchedulerApi } from '../../../common/hooks/use-social-scheduler-api.hook';
import { useContentStudioApi } from '../../../common/hooks/use-content-studio-api.hook';
import { socialReadPending } from './brand-completeness';

/**
 * "Your posts have not been read yet."
 *
 * Connecting an account is the one moment new brand evidence appears, and
 * until now nothing said so — the kit went on describing a brand it had only
 * ever read from a website, while the vendor's own captions sat unread.
 *
 * It offers rather than acts. Reading spends credits, and a paid job firing
 * because someone linked an account is not a decision to make on their behalf.
 * The toast is the offer; pressing it is the consent.
 *
 * Renders nothing — sonner owns the surface, the same one the agent's work
 * toasts use, so this arrives where the vendor already looks for "something
 * is happening".
 */

/** Long enough to be seen after the redirect settles, short enough to feel connected to it. */
const APPEAR_AFTER_MS = 1500;

export const SocialBrandNudge: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const social = useSocialSchedulerApi();
    const studio = useContentStudioApi();

    /*
     * Once per load. The check is cheap but the toast is not: a second copy of
     * the same suggestion is worse than none, and re-mounting on navigation
     * would produce exactly that.
     */
    const asked = useRef(false);

    useEffect(() => {
        if (asked.current) return;
        asked.current = true;

        let cancelled = false;

        const timer = setTimeout(async () => {
            try {
                const [accounts, kit] = await Promise.all([
                    social.listAccounts({ status: 'active', limit: 20 }).then((r) => r.items || []),
                    studio.getBrandKit().catch(() => null),
                ]);

                if (cancelled || !socialReadPending(kit as any, accounts)) return;

                toast.message(t('BRAND.NUDGE.TITLE', 'Your posts have not been read yet'), {
                    id: 'social-brand-unread',
                    description: t(
                        'BRAND.NUDGE.BODY',
                        'Reading your own captions tells us more about how you write than a website can.',
                    ) as string,
                    duration: 12000,
                    action: {
                        label: t('BRAND.NUDGE.ACTION', 'Read my posts') as string,
                        // Straight to the brand page, which owns the run and
                        // the credit quote. Starting it from a toast would
                        // spend without showing what it costs.
                        onClick: () => navigate('/marketing/brand?read=social'),
                    },
                });
            } catch {
                // A suggestion that cannot be made is not an error worth
                // showing. The brand page says the same thing in place.
            }
        }, APPEAR_AFTER_MS);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
};
